// src/components/common/forms/EntityFormHeader.tsx
import React from 'react';
import { X, Save, LucideIcon } from 'lucide-react';

export interface BadgeConfig {
  show: boolean | ((entity: Record<string, unknown>) => boolean);
  label: string;
  icon?: LucideIcon;
  color?: string;
}

interface EntityFormHeaderProps {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  gradient?: string;
  badges?: BadgeConfig[];
  hasChanges?: boolean;
  saving?: boolean;
  onSave?: () => void;
  onClose: () => void;
  entity?: Record<string, unknown>;
}

export const EntityFormHeader: React.FC<EntityFormHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  gradient = 'from-blue-500 to-purple-600',
  badges = [],
  hasChanges = false,
  saving = false,
  onSave,
  onClose,
  entity,
}) => {
  const shouldShowBadge = (badge: BadgeConfig) => {
    if (typeof badge.show === 'function') {
      return entity ? badge.show(entity) : false;
    }
    return badge.show;
  };

  return (
    <div className={`flex justify-between items-center p-6 border-b bg-gradient-to-r ${gradient} text-white`}>
      <div className="flex items-center space-x-4">
        {Icon && (
          <div className="h-12 w-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
            <Icon className="h-6 w-6" />
          </div>
        )}
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          {subtitle && <p className="text-blue-100">{subtitle}</p>}
        </div>
        {badges.map((badge, index) => {
          if (!shouldShowBadge(badge)) return null;

          const BadgeIcon = badge.icon;
          return (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white bg-opacity-20"
            >
              {BadgeIcon && <BadgeIcon className="h-4 w-4 mr-1" />}
              {badge.label}
            </span>
          );
        })}
      </div>
      <div className="flex items-center space-x-2">
        {hasChanges && onSave && (
          <button
            onClick={onSave}
            disabled={saving}
            className="bg-white text-blue-600 px-4 py-2 rounded-md hover:bg-gray-50 flex items-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
          </button>
        )}
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};